/**
 * Get a photo ready to upload.
 *
 * Phone photos are often larger than the 5MB upload limit, and some arrive in formats the
 * server refuses (HEIC). Instead of rejecting them, the photo is redrawn in the browser:
 * longest side at most 2048px, saved as JPEG, stepping the quality down until it is under
 * 4MB. Small JPG/PNG/WEBP/GIF files are passed through untouched.
 *
 * Throws a fan-readable Error when the browser cannot read the file at all.
 */

const MAX_SIDE = 2048
const TARGET_BYTES = 4 * 1024 * 1024
const PASS_THROUGH_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

export const UNREADABLE_PHOTO =
  "We couldn't read this photo. Please choose a JPG or PNG photo (or take a screenshot of it and upload that)."

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(UNREADABLE_PHOTO)) }
    img.src = url
  })
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality))
}

export async function prepareImageForUpload(file: File): Promise<File> {
  if (PASS_THROUGH_TYPES.includes(file.type) && file.size <= TARGET_BYTES) return file

  const looksLikeImage = file.type.startsWith('image/') || /\.(heic|heif|jpe?g|png|webp|gif)$/i.test(file.name)
  if (!looksLikeImage) throw new Error('Please choose a photo (JPG or PNG).')

  const img = await loadImage(file)
  const width = img.naturalWidth || img.width
  const height = img.naturalHeight || img.height
  if (!width || !height) throw new Error(UNREADABLE_PHOTO)

  const scale = Math.min(1, MAX_SIDE / Math.max(width, height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width * scale))
  canvas.height = Math.max(1, Math.round(height * scale))
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error(UNREADABLE_PHOTO)
  // JPEG has no transparency: paint white under PNG/GIF transparency instead of black
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

  let blob: Blob | null = null
  for (const quality of [0.85, 0.75, 0.65, 0.5]) {
    blob = await toBlob(canvas, quality)
    if (blob && blob.size <= TARGET_BYTES) break
  }
  if (!blob) throw new Error(UNREADABLE_PHOTO)

  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo'
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() })
}
